import * as Sentry from "@sentry/react";
import { z } from "zod";

import type { Asset as OptinsAsset } from "@/api/assets";
import type { GlobalStyleProps } from "@grapesjs/studio-sdk";

export type OnboardingStepId =
  | "intro"
  | "styling"
  | "operation"
  | "components"
  | "voice"
  | "scope"
  | "complete"
  | "results";

// --- Response schemas (FOG-2079) -------------------------------------------
//
// Response schemas are `.passthrough()`: sections round-trip (parse → cache →
// form → POST on save) and default-strict zod strips unknown keys — silent
// data loss on save. Exception: setupMetaSchema stays plain so `keyof
// SetupMeta` stays the literal union STEP_META_KEY_MAP relies on.

// Opaque GrapesJS asset blob — deliberately unvalidated (api/assets is out of
// scope); a typed pass-through exactly like the cast it replaces.
export const assetDataSchema = z.custom<AssetData>();

export const valuePropSchema = z
  .object({
    title: z.string(),
    body: z.string(),
    asset: assetDataSchema,
  })
  .passthrough();

export const groupingSchema = z
  .object({
    name: z.string(),
    url: z.string(),
  })
  .passthrough();

export const socialSchema = z
  .object({
    name: z.string(),
    url: z.string(),
    asset: assetDataSchema,
  })
  .passthrough();

export const linkSchema = z
  .object({
    name: z.string(),
    url: z.string(),
    asset: assetDataSchema,
  })
  .passthrough();

export const setupIntroSchema = z
  .object({
    assets: z.array(assetDataSchema).optional(),
    domain: z.string().optional(),
  })
  .passthrough()
  .nullable();

// One section divider Prisma read off the brand's real emails (branddetails-v2
// styling.dividers): a full-width strip image with the flat colour of its top
// and bottom pixel row ("" when transparent or patterned). `edge` = the rows
// differ, so it joins the surface above to the one below and the preview tiles
// place it by matching their colours; `strip` = a ribbon, line or heading
// rendered as an image, offered in the brand book and the picker only.
export const setupDividerSchema = z
  .object({
    src: z.string(),
    width: z.number(),
    height: z.number(),
    topColor: z.string().nullable(),
    bottomColor: z.string().nullable(),
    kind: z.string(),
    band: z.string().nullable(),
    uses: z.number(),
  })
  .passthrough();
export type SetupDivider = z.infer<typeof setupDividerSchema>;

const setupStylingFieldsSchema = z.object({
  alertBannerBackground: z.string().optional(),
  bannerBackground: z.string().optional(),
  bodyFontFamily: z.string().optional(),
  buttonPrimaryBg: z.string().optional(),
  buttonPrimaryBorderColor: z.string().optional(),
  buttonPrimaryBorderRadius: z.string().optional(),
  buttonPrimaryBorderStyle: z.string().optional(),
  buttonPrimaryBorderWidth: z.string().optional(),
  buttonPrimaryPadding: z.string().optional(),
  buttonPrimaryText: z.string().optional(),
  buttonSecondaryBg: z.string().optional(),
  buttonSecondaryBorderColor: z.string().optional(),
  buttonSecondaryBorderRadius: z.string().optional(),
  buttonSecondaryBorderStyle: z.string().optional(),
  buttonSecondaryBorderWidth: z.string().optional(),
  buttonSecondaryPadding: z.string().optional(),
  buttonSecondaryText: z.string().optional(),
  buttonTertiaryBg: z.string().optional(),
  buttonTertiaryBorderColor: z.string().optional(),
  buttonTertiaryBorderRadius: z.string().optional(),
  buttonTertiaryBorderStyle: z.string().optional(),
  buttonTertiaryBorderWidth: z.string().optional(),
  buttonTertiaryPadding: z.string().optional(),
  buttonTertiaryText: z.string().optional(),
  canvasBackground: z.string().optional(),
  darkLogo: assetDataSchema.optional(),
  fontSizeDesktopDefault: z.string().optional(),
  fontSizeDesktopL: z.string().optional(),
  fontSizeDesktopM: z.string().optional(),
  fontSizeDesktopOversize: z.string().optional(),
  fontSizeDesktopS: z.string().optional(),
  fontSizeDesktopXL: z.string().optional(),
  fontSizeDesktopXS: z.string().optional(),
  fontSizeMobileL: z.string().optional(),
  fontSizeMobileM: z.string().optional(),
  fontSizeMobileOversize: z.string().optional(),
  fontSizeMobileS: z.string().optional(),
  fontSizeMobileXL: z.string().optional(),
  fontSizeMobileXS: z.string().optional(),
  // Site chrome measured by Prisma (header/footer; branddetails-v2 #71 + #73).
  // "" when unmeasured — the registry's inheritFrom fallback then keeps the
  // brand book rendering as it did before these keys existed. The band's
  // background image (an absolute http(s) URL, never a gradient), its
  // placement trio and the band / link fonts are typed so the brand book
  // round-trips them; no registry field wears them yet (see useFormData's
  // server-key passthrough for why they still survive a save).
  footerBackground: z.string().optional(),
  footerBackgroundImage: z.string().optional(),
  footerBackgroundPosition: z.string().optional(),
  footerBackgroundRepeat: z.string().optional(),
  footerBackgroundSize: z.string().optional(),
  footerBorderColor: z.string().optional(),
  footerBorderWidth: z.string().optional(),
  footerFontFamily: z.string().optional(),
  footerFontSize: z.string().optional(),
  footerFontWeight: z.string().optional(),
  footerLinkColor: z.string().optional(),
  footerLinkFontFamily: z.string().optional(),
  footerLinkFontSize: z.string().optional(),
  footerLinkFontWeight: z.string().optional(),
  footerLinkLetterSpacing: z.string().optional(),
  footerLinkTextTransform: z.string().optional(),
  footerPadding: z.string().optional(),
  footerTextColor: z.string().optional(),
  headerBackground: z.string().optional(),
  headerBackgroundImage: z.string().optional(),
  headerBackgroundPosition: z.string().optional(),
  headerBackgroundRepeat: z.string().optional(),
  headerBackgroundSize: z.string().optional(),
  headerBorderColor: z.string().optional(),
  headerBorderWidth: z.string().optional(),
  headerFontFamily: z.string().optional(),
  headerFontSize: z.string().optional(),
  headerFontWeight: z.string().optional(),
  headerLinkColor: z.string().optional(),
  headerLinkFontFamily: z.string().optional(),
  headerLinkFontSize: z.string().optional(),
  headerLinkFontWeight: z.string().optional(),
  headerLinkLetterSpacing: z.string().optional(),
  headerLinkTextTransform: z.string().optional(),
  headerPadding: z.string().optional(),
  headerTextColor: z.string().optional(),
  headlineDarkColor: z.string().optional(),
  headlineFontFamily: z.string().optional(),
  headlineFontWeight: z.string().optional(),
  headlineLightColor: z.string().optional(),
  icon: assetDataSchema.optional(),
  infoBackground: z.string().optional(),
  lightLogo: assetDataSchema.optional(),
  logoWidth: z.string().optional(),
  outlineColor: z.string().optional(),
  productBackground: z.string().optional(),
  textDarkColor: z.string().optional(),
  textLightColor: z.string().optional(),
  textLinkColor: z.string().optional(),
  textLinkUnderline: z.number().optional(),
  webFontFamily: z.string().optional(),
  webFontUrl: z.string().optional(),
  bodyWebFontFamily: z.string().optional(),
  bodyWebFontUrl: z.string().optional(),
  wrapperBackground: z.string().optional(),
  // The list is Go-only like `icon`; nullable because Go marshals a nil slice as null.
  // A malformed entry drops the list rather than failing the whole setup parse.
  dividers: z.array(setupDividerSchema).nullable().optional().catch(null),
});

// Known styling keys — `keyof SetupStyling` collapses to string | number
// under passthrough.
export type SetupStylingFieldId = keyof z.infer<
  typeof setupStylingFieldsSchema
>;

export const setupStylingSchema = setupStylingFieldsSchema
  .passthrough()
  .nullable();

export const setupOperationSchema = z
  .object({
    containers: z.array(valuePropSchema).optional(),
    heroAsset: z.string().nullable().optional(),
    heroBanner: z.string().optional(),
    informationAddress: z.string().optional(),
    informationEmail: z.string().optional(),
    informationName: z.string().optional(),
    informationPhone: z.string().optional(),
    links: z.array(linkSchema).optional(),
    messagingAdditional: z.string().optional(),
    messagingDisclaimer: z.string().optional(),
    messagingFreeShipping: z.string().optional(),
    messagingTerms: z.string().optional(),
    socials: z.array(socialSchema).optional(),
    groupings: z.array(groupingSchema).optional(),
    title: z.string().optional(),
    currencySymbol: z.string().optional(),
    currencyFormat: z.string().optional(),
    currencySymbolPosition: z.string().optional(),
  })
  .passthrough()
  .nullable();

export const setupVoiceSchema = z
  .object({
    assets: z.array(assetDataSchema).optional(),
    emojiAvoidTokens: z.array(z.string()).optional(),
    emojiBody: z.number().optional(),
    emojiCta: z.number().optional(),
    emojiHeadline: z.number().optional(),
    emojiPreferredTokens: z.array(z.string()).optional(),
    emojiPreheadline: z.number().optional(),
    emojiSubheadline: z.number().optional(),
    emojiSubject: z.number().optional(),
    emojiUsage: z.number().optional(),
    messagingAudience: z.string().optional(),
    messagingAvoidTokens: z.array(z.string()).optional(),
    messagingLanguage: z.string().optional(),
    messagingPreferredTokens: z.array(z.string()).optional(),
    tone: z.string().optional(),
  })
  .passthrough()
  .nullable();

// Per-component style tokens captured by the Components step. Values are CSS
// strings; the backend seeds every key as "" until the step is saved.
export const setupComponentsSchema = z
  .object({
    productGrids: z.record(z.string()).optional(),
    offerCodes: z.record(z.string()).optional(),
  })
  .passthrough()
  .nullable();

// The backend sends `null` (not an omitted key) for sections never touched.
export const setupMetaSchema = z.object({
  approveUpdatedAt: z.string().nullable().optional(),
  approveUpdatedBy: z.string().nullable().optional(),
  componentsUpdatedAt: z.string().nullable().optional(),
  componentsUpdatedBy: z.string().nullable().optional(),
  introUpdatedAt: z.string().nullable().optional(),
  introUpdatedBy: z.string().nullable().optional(),
  operationUpdatedAt: z.string().nullable().optional(),
  operationUpdatedBy: z.string().nullable().optional(),
  stylingUpdatedAt: z.string().nullable().optional(),
  stylingUpdatedBy: z.string().nullable().optional(),
  voiceUpdatedAt: z.string().nullable().optional(),
  voiceUpdatedBy: z.string().nullable().optional(),
});

// Sections may legitimately be absent (as_of / default snapshots) — default
// to the `null` consumers already handle; garbage in a present section throws.
export const setupResponseSchema = z
  .object({
    approve: z.array(z.unknown()).optional(),
    components: setupComponentsSchema.default(null),
    intro: setupIntroSchema.default(null),
    // Which source led branddetails' resolution: prisma | extracting |
    // unavailable | legacy. "extracting" = Prisma is ingesting the domain.
    brandSource: z.string().nullable().optional(),
    // preprocess, not .default(): re-map a site sends meta: null, not absent
    meta: z.preprocess((v) => v ?? {}, setupMetaSchema),
    operation: setupOperationSchema.default(null),
    styling: setupStylingSchema.default(null),
    voice: setupVoiceSchema.default(null),
  })
  .passthrough();

export type SetupIntro = z.infer<typeof setupIntroSchema>;
export type SetupStyling = z.infer<typeof setupStylingSchema>;
export type SetupOperation = z.infer<typeof setupOperationSchema>;
export type SetupVoice = z.infer<typeof setupVoiceSchema>;
export type SetupComponents = z.infer<typeof setupComponentsSchema>;
export type SetupMeta = z.infer<typeof setupMetaSchema>;
export type SetupResponse = z.infer<typeof setupResponseSchema>;

export type AssetData = OptinsAsset | null;

export type ValueProp = z.infer<typeof valuePropSchema>;
export type Grouping = z.infer<typeof groupingSchema>;
export type Social = z.infer<typeof socialSchema>;
export type Link = z.infer<typeof linkSchema>;

// Tile variant types for default creative generation

export type DefaultCreativeRequest = {
  setup: {
    intro: SetupIntro;
    voice: SetupVoice;
  };
  frames: FrameVariant[];
};

// Backend sends many more per-variant fields; only `results` is read.
export const generatedFrameVariantResultSchema = z
  .object({
    results: z.array(z.record(z.string())),
  })
  .passthrough();

export type GeneratedFrameVariantResult = z.infer<
  typeof generatedFrameVariantResultSchema
>;

export const defaultCreativeResponseSchema = z
  .object({
    domain: z.string(),
    frames: z.record(generatedFrameVariantResultSchema),
  })
  .passthrough();

export type DefaultCreativeResponse = z.infer<
  typeof defaultCreativeResponseSchema
>;

export const productPreviewSchema = z
  .object({
    imageKey: z.string(),
    imageUrl: z.string(),
    name: z.string(),
  })
  .passthrough();

export type ProductPreview = z.infer<typeof productPreviewSchema>;

export const productPreviewsResponseSchema = z
  .object({
    domain: z.string(),
    products: z.array(productPreviewSchema),
  })
  .passthrough();

export type ProductPreviewsResponse = z.infer<
  typeof productPreviewsResponseSchema
>;

export const frameVariantSchema = z
  .object({
    // branddetails-v2 models every column as a *string, so a blank sheet
    // cell arrives as null, not as a missing key.
    defaultCopy: z.record(z.string()).nullish(),
    frameName: z.string().nullish(),
    frameSlug: z.string().nullish(),
    key: z.string(),
    tileConfiguration: z.string().nullish(),
    tileDescription: z.string().nullish(),
    tileKey: z.string().nullish(),
    tileName: z.string().nullish(),
  })
  .passthrough();

export type FrameVariant = z.infer<typeof frameVariantSchema>;

// The catalog is a hand-edited Google Sheet: drop malformed rows rather than
// letting one bad row fail the whole Brand Book (FOG-2593).
export const frameVariantsResponseSchema = z
  .array(z.unknown())
  .transform((rows) => {
    const dropped: Array<{ index: number; issues: z.ZodIssue[] }> = [];
    const variants = rows.flatMap((row, index) => {
      const parsed = frameVariantSchema.safeParse(row);
      if (parsed.success) return [parsed.data];
      dropped.push({ index, issues: parsed.error.issues });
      return [];
    });
    if (dropped.length > 0) {
      Sentry.captureMessage("Dropped malformed rows from the frames catalog", {
        level: "warning",
        extra: { dropped },
      });
    }
    return variants;
  });

export type FrameVariantsResponse = z.infer<typeof frameVariantsResponseSchema>;

// GET /creative/copy_coverage: templates-site blueprints binding
// generated_content keys D20 has no copy for. `reason` is a frame_copy skip
// code when the whole row is missing, null when only some cells are empty.
export const copyCoverageSchema = z.array(
  z
    .object({
      frameId: z.string(),
      frameName: z.string().nullish(),
      frameSlug: z.string().nullish(),
      reason: z.string().nullish(),
      missingKeys: z.array(z.string()),
      // The D20 frames tab the backend reads, so "Open D20" can't drift from it.
      sheetUrl: z.string(),
    })
    .passthrough(),
);

export type CopyCoverage = z.infer<typeof copyCoverageSchema>;

const applyToFramesFrameStatusSchema = z
  .object({
    copy: z.record(z.string()).optional(),
    patch_success: z.boolean().optional(),
    render_error: z.string().nullable().optional(),
    skip_reason: z.string().nullable().optional(),
    success: z.boolean().optional(),
    error: z.string().nullable().optional(),
  })
  .passthrough();

const applyToFramesBlockStatusSchema = z
  .object({
    patch_success: z.boolean().optional(),
    render_error: z.string().nullable().optional(),
    skip_reason: z.string().nullable().optional(),
    success: z.boolean().optional(),
    error: z.string().nullable().optional(),
  })
  .passthrough();

// Never `.parse`d — this arrives via the SSE stream (applyStreamEvent.ts),
// which validates by hand; the schema is the type's source of truth.
export const applyToFramesResponseSchema = z
  .object({
    success: z.boolean().optional(),
    has_partial_failure: z.boolean().optional(),
    status: z
      .object({
        frames: z
          .object({
            success: z.boolean().optional(),
            error: z.string().nullable().optional(),
          })
          .passthrough()
          .optional(),
        blocks: z
          .object({
            success: z.boolean().optional(),
            error: z.string().nullable().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .optional(),
    _debug: z
      .object({
        copy: z.record(z.record(z.string())).optional(),
        frame_id_slugs: z.record(z.string()).optional(),
        frames: z
          .union([
            z.array(z.unknown()),
            z
              .object({
                frame_id_slugs: z.record(z.string()).optional(),
                frames: z
                  .array(
                    z
                      .object({
                        id: z.string().optional(),
                        name: z.string().optional(),
                        slug: z.string().optional(),
                      })
                      .passthrough(),
                  )
                  .optional(),
                timing: z.record(z.number()).optional(),
              })
              .passthrough(),
          ])
          .optional(),
        original_frame_id_slugs: z.record(z.string()).optional(),
        prompt: z.unknown().optional(),
        setup: setupResponseSchema.optional(),
        timing: z.record(z.number()).optional(),
        blocks: z
          .object({
            blocks: z
              .array(
                z
                  .object({
                    id: z.string().optional(),
                    name: z.string().optional(),
                  })
                  .passthrough(),
              )
              .optional(),
            timing: z.record(z.number()).optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .optional(),
    frames: z.record(applyToFramesFrameStatusSchema),
    blocks: z.record(applyToFramesBlockStatusSchema).optional(),
  })
  .passthrough();

export type ApplyToFramesResponse = z.infer<typeof applyToFramesResponseSchema>;

export type OnboardingFormValue =
  | null
  | string
  | number
  | boolean
  | AssetData
  | SetupDivider[]
  | ValueProp[]
  | Grouping[]
  | Social[]
  | Link[]
  | string[];

export interface BrandKitField extends GlobalStyleProps {
  // don't make optional?
  dsKey: string;
  tooltip?: string;
  /** Registry id whose current value fills this field when the brand book has none. */
  inheritFrom?: string;
  category: {
    id: string;
    label: string;
    // don't make optional?
    dsKey: string;
  };
}

// --- Brand kits (branddetails-v2 PR #40) ------------------------------------

// One entry in the site's kit registry. The implicit default kit is always
// first with { id: "default", name: "Default", createdAt: null, createdBy: "" }.
export const setupKitDetailSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    // RFC3339, null for the implicit default kit
    createdAt: z.string().nullable(),
    createdBy: z.string(),
  })
  .passthrough();

export type SetupKitDetail = z.infer<typeof setupKitDetailSchema>;

// Response shape of GET/POST /{siteId}/setup/kits and DELETE …/kits/{kitId}.
export const setupKitsDetailSchema = z
  .object({
    kits: z.array(setupKitDetailSchema),
    // kit recorded by the newest copy-applied marker
    appliedKit: z.string().nullable().default(null),
  })
  .passthrough();

export type SetupKitsDetail = z.infer<typeof setupKitsDetailSchema>;

export interface SetupKitCreateRequest {
  id: string;
  name: string;
  copyFrom: string; // must be "default" or an existing kit id
}

// Response shape of GET /{siteId}/setup/frame_copy: the per-tile copy produced
// by the most recent apply that actually generated copy. 404 before the first
// such apply (and on backends without the route) — consumers self-hide. No
// ?kit= filter exists; `kit` says which kit produced the artifact. Frame
// values are loosely typed (Go stores map[string]any) — render strings only.
export const frameCopyDetailSchema = z
  .object({
    appliedAt: z.string(), // RFC3339
    kit: z.string(),
    voiceHash: z.string().nullable(),
    frames: z.record(z.record(z.unknown())),
    // frameId → copy-skip reason code for frames in the apply run that got no
    // generated copy (see copySkipMessage). Absent/empty on artifacts written
    // before skip reporting existed.
    skipped: z.record(z.string()).optional(),
  })
  .passthrough();

export type FrameCopyDetail = z.infer<typeof frameCopyDetailSchema>;

// --- Brand-book history & revert (branddetails-v2 PR #44) -------------------

// `kind` is z.string(), not z.enum: a new backend kind must not crash the
// feed — unknown kinds render as plain rows.
export const SETUP_HISTORY_ENTRY_KINDS = [
  "save",
  "revert",
  "apply_to_frames",
  "snapshot",
] as const;

// One row of GET /{siteId}/setup/history. The feed covers the DEFAULT kit's
// sections; apply rows exist for every kit (all kits' apply markers share one
// storage prefix) and carry `kit` so the UI can label or filter them.
export const setupHistoryEntryDetailSchema = z
  .object({
    section: z.string(), // intro | styling | voice | operation | copy_applied
    versionId: z.string(),
    at: z.string(), // RFC3339
    by: z.string(), // "" when unknown (e.g. legacy-service applies)
    kind: z.string(), // see SETUP_HISTORY_ENTRY_KINDS
    revertedFrom: z.string(), // versionId; "" unless kind === "revert"
    empty: z.boolean(), // stored payload was null/empty (reset-style write)
    revertable: z.boolean(), // false for apply_to_frames rows
    kit: z.string(), // "default" on save/revert rows
    // Tiles that received copy in an apply run; null on save/revert rows and on
    // apply rows without a frame_copy artifact (legacy-service applies,
    // pre-artifact history, voice-unchanged skip applies).
    framesUpdated: z.number().nullable(),
    // A snapshot row's optional user-given name; null on every non-snapshot row
    // and on unnamed snapshots (the UI falls back to "Save point").
    label: z.string().nullable(),
  })
  .passthrough();

export type SetupHistoryEntryDetail = z.infer<
  typeof setupHistoryEntryDetailSchema
>;

export const setupHistoryDetailSchema = z
  .object({
    entries: z.array(setupHistoryEntryDetailSchema),
    hasMore: z.boolean(),
  })
  .passthrough();

export type SetupHistoryDetail = z.infer<typeof setupHistoryDetailSchema>;

// Response of GET /{siteId}/setup/history/{section}/{versionId}. For the four
// sections `payload` is the typed section (or null); for section `frame_copy`
// it is the raw per-tile copy artifact ({applied_at, applied_by, frames,
// skipped, voice_hash}[, kit]) of one apply run.
export const setupHistoryVersionDetailSchema = z
  .object({
    section: z.string(),
    versionId: z.string(),
    at: z.string(), // RFC3339
    by: z.string(),
    kind: z.string(),
    revertedFrom: z.string(),
    payload: z.unknown(),
  })
  .passthrough();

export type SetupHistoryVersionDetail = z.infer<
  typeof setupHistoryVersionDetailSchema
>;

// Body of POST /{siteId}/setup/revert — exactly one mode:
// per-tile {section, versionId} or complete {at} (the panel's Restore).
export type SetupRevertRequest =
  | { section: string; versionId: string }
  | { at: string };

// Body of POST /{siteId}/setup/snapshot ("save progress"). label is trimmed
// server-side; empty stores as null; > 120 runes → 400.
export interface SetupSnapshotCreateRequest {
  label: string;
}

// Response of DELETE /{siteId}/setup/snapshot/{versionId}.
export const setupSnapshotDeleteDetailSchema = z
  .object({
    deleted: z.string(),
  })
  .passthrough();

export type SetupSnapshotDeleteDetail = z.infer<
  typeof setupSnapshotDeleteDetailSchema
>;
